"use client";
import React, { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import axiosInstance from "@/lib/utils/axiosInstance";
import { getSpecialties } from "@/services/preceptor-service";

interface PreceptorFormData {
  fullName: string;
  titles: string[];
  email: string;
  phoneNumber: string;
  specialty: string;
  siteName: string;
  siteAddress: string;
  siteLatitude: number | null;
  siteLongitude: number | null;
  preceptedBefore: string;
  schedule: string;
  patientPopulation: string[];
  track: string;
  // New professional credential fields
  licenseNumber: string;
  licenseState: string;
  licenseIssueDate: string;
  licenseExpirationDate: string;
  graduateSchool: string;
  graduationDate: string;
  degreeConcentration: string;
  currentPosition: string;
  currentPositionStartDate: string;
}

interface Specialty {
  id: number | string;
  name: string;
}

const PATIENT_POPULATIONS = [
  "Pediatric (0-17)",
  "Young Adult (18-30)",
  "Adult (30-65)",
  "Geriatric (65+)",
  "Mixed Age Groups",
];

const SUGGESTED_TITLES = [
  "MD",
  "DO",
  "NP",
  "PA-C",
  "DNP",
  "CRNA",
  "RN",
  "PharmD",
];

const MAX_TITLES = 10;

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC"
];

interface BecomePreceptorFormProps {
  track?: string;
}

export function
    BecomePreceptorForm({ track = "np" }: BecomePreceptorFormProps) {
  const router = useRouter();
  const [formData, setFormData] = useState<PreceptorFormData>({
    fullName: "",
    titles: [],
    email: "",
    phoneNumber: "",
    specialty: "",
    siteName: "",
    siteAddress: "",
    siteLatitude: null,
    siteLongitude: null,
    preceptedBefore: "",
    schedule: "",
    patientPopulation: [],
    track,
    // New professional credential fields
    licenseNumber: "",
    licenseState: "",
    licenseIssueDate: "",
    licenseExpirationDate: "",
    graduateSchool: "",
    graduationDate: "",
    degreeConcentration: "",
    currentPosition: "",
    currentPositionStartDate: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [loadingSpecialties, setLoadingSpecialties] = useState(true);
  const [titlesInput, setTitlesInput] = useState("");

  const parseTitles = (raw: string): string[] => {
    return Array.from(
      new Set(
        raw
          .split(/[,;]/)
          .map((entry) => entry.trim())
          .filter(Boolean)
      )
    )
      .map((entry) => entry.replace(/\s+/g, " "))
      .slice(0, MAX_TITLES);
  };

  const handleTitlesInputChange = (value: string) => {
    setTitlesInput(value);
    const parsed = parseTitles(value);
    setFormData((prev) => ({
      ...prev,
      titles: parsed,
    }));
  };

  const handleSuggestedTitleClick = (title: string) => {
    const parsedExisting = parseTitles(titlesInput);

    if (parsedExisting.some((entry) => entry.toLowerCase() === title.toLowerCase())) {
      return;
    }

    const next = [...parsedExisting, title].slice(0, MAX_TITLES);
    setFormData((prev) => ({
      ...prev,
      titles: next,
    }));
    setTitlesInput(next.join(", "));
  };

  // Fetch specialties on component mount
  useEffect(() => {
    const fetchSpecialties = async () => {
      try {
        const data = await getSpecialties();
        setSpecialties(data || []);
      } catch (err) {
        console.error("Failed to fetch specialties:", err);
        setSpecialties([]);
      } finally {
        setLoadingSpecialties(false);
      }
    };

    fetchSpecialties();
  }, []);

  // Phone validation utilities
  const validateUSPhoneNumber = (phone: string): boolean => {
    // US phone: (XXX) XXX-XXXX, XXX-XXX-XXXX, XXXXXXXXXX, +1 (XXX) XXX-XXXX, etc.
    const usPhoneRegex = /^(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;
    return usPhoneRegex.test(phone.replace(/\s/g, ""));
  };

  const validateInternationalPhoneNumber = (phone: string): boolean => {
    // International phone: allows + followed by 7-15 digits, with optional formatting
    const internationalPhoneRegex = /^(\+)?[0-9\s\-\.()]{7,}$/;
    return internationalPhoneRegex.test(phone);
  };

  const validatePhoneNumber = (phone: string): boolean => {
    if (track === "img") {
      // For IMG, any valid international format
      return validateInternationalPhoneNumber(phone);
    } else {
      // For NP and PA, validate US format
      return validateUSPhoneNumber(phone);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePatientPopulationChange = (population: string) => {
    setFormData((prev) => ({
      ...prev,
      patientPopulation: prev.patientPopulation.includes(population)
        ? prev.patientPopulation.filter((p) => p !== population)
        : [...prev.patientPopulation, population],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Validate phone number based on track
    if (!validatePhoneNumber(formData.phoneNumber)) {
      const phoneErrorMsg = track === "img"
        ? "Please enter a valid international phone number (e.g., +44 20 7946 0958)"
        : "Please enter a valid US phone number (e.g., (555) 123-4567)";
      setError(phoneErrorMsg);
      setLoading(false);
      return;
    }

    if (formData.titles.length === 0) {
      setError("Please add at least one professional title.");
      setLoading(false);
      return;
    }

    // Validate license dates (required)
    if (!formData.licenseIssueDate) {
      setError("Please enter your license issue date.");
      setLoading(false);
      return;
    }
    if (!formData.licenseExpirationDate) {
      setError("Please enter your license expiration date.");
      setLoading(false);
      return;
    }
    if (new Date(formData.licenseExpirationDate) <= new Date(formData.licenseIssueDate)) {
      setError("License expiration date must be after issue date.");
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.post("/preceptor-applications", formData);

      if (response.data) {
        setSuccess(true);
        // Show success message and redirect after 2 seconds
        setTimeout(() => {
          router.push(`/auth/preceptor/application-submitted?track=${track}`);
        }, 2000);
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      setError(
        error.response?.data?.message || error.message || "Failed to submit application. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-green-600 text-5xl">✓</div>
          <h2 className="text-2xl font-bold">Application Submitted</h2>
          <p className="text-gray-600">
            Thank you! Our team will review your application and create your account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden py-12">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-indigo-50 to-white" />
        <div className="absolute top-[-240px] left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] rounded-full bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.20),transparent_70%)]" />
      </div>

      <div className="relative max-w-2xl mx-auto px-6 md:px-10 space-y-8">
        <div className="text-center space-y-3">
          <h1 className="text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-cyan-500">
            Become a Preceptor
          </h1>
          <p className="text-sm text-gray-600">
            Join our network and mentor healthcare students through clinical rotations.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
          {error && (
            <div className="p-4 rounded-lg bg-red-50 border border-red-200">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Full Name and Title */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                required
                placeholder="e.g., John Smith"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Professional Titles *
              </label>
              <input
                type="text"
                value={titlesInput}
                onChange={(event) => handleTitlesInputChange(event.target.value)}
                required
                placeholder="e.g., MD, NP, PA-C"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Separate multiple titles with commas. You may add up to {MAX_TITLES} titles.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {SUGGESTED_TITLES.map((title) => {
                  const isSelected = formData.titles.some(
                    (entry) => entry.toLowerCase() === title.toLowerCase()
                  );

                  return (
                    <button
                      key={title}
                      type="button"
                      onClick={() => handleSuggestedTitleClick(title)}
                      className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                        isSelected
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-300 bg-white text-gray-600 hover:border-blue-400 hover:text-blue-600"
                      }`}
                    >
                      {title}
                    </button>
                  );
                })}
              </div>
              {formData.titles.length > 0 && (
                <p className="mt-2 text-xs text-gray-600">
                  Selected: {formData.titles.join(", ")}
                </p>
              )}
            </div>
          </div>

          {/* Email and Phone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="you@example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                required
                placeholder={track === "img" ? "+44 20 7946 0958" : "(123) 456-7890"}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                {track === "img"
                  ? "International format accepted"
                  : "US format required"}
              </p>
            </div>
          </div>

          {/* Specialty */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primary Specialty *
            </label>
            <select
              name="specialty"
              value={formData.specialty}
              onChange={handleInputChange}
              required
              disabled={loadingSpecialties}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">
                {loadingSpecialties ? "Loading specialties..." : "Select a specialty"}
              </option>
              {specialties.map((spec) => (
                <option key={spec.id} value={spec.name}>
                  {spec.name}
                </option>
              ))}
            </select>
          </div>

          {/* Site Information */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Practice / Site Information *
            </label>
            <p className="text-xs text-gray-500 mt-1">
              Enter your practice or site details directly.
            </p>
          </div>

          {/* Site details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Site/Practice Name *
              </label>
              <input
                type="text"
                name="siteName"
                value={formData.siteName}
                onChange={handleInputChange}
                required
                placeholder="e.g., Primary Care Clinic"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Site Address *
              </label>
              <input
                type="text"
                name="siteAddress"
                value={formData.siteAddress}
                onChange={handleInputChange}
                required
                placeholder="Street, City, State, ZIP"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* License Information */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">License Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  License Number
                </label>
                <input
                  type="text"
                  name="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={handleInputChange}
                  placeholder="e.g., RN123456"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  License State
                </label>
                <select
                  name="licenseState"
                  value={formData.licenseState}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select State</option>
                  {US_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  License Issue Date *
                </label>
                <input
                  type="date"
                  name="licenseIssueDate"
                  value={formData.licenseIssueDate}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  License Expiration Date *
                </label>
                <input
                  type="date"
                  name="licenseExpirationDate"
                  value={formData.licenseExpirationDate}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Education Information */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Education (Optional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Graduate School/University
                </label>
                <input
                  type="text"
                  name="graduateSchool"
                  value={formData.graduateSchool}
                  onChange={handleInputChange}
                  placeholder="e.g., Johns Hopkins University"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Graduation Date
                </label>
                <input
                  type="date"
                  name="graduationDate"
                  value={formData.graduationDate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Degree Concentration/Specialty
                </label>
                <input
                  type="text"
                  name="degreeConcentration"
                  value={formData.degreeConcentration}
                  onChange={handleInputChange}
                  placeholder="e.g., Family Nurse Practitioner"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Current Position */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Current Position (Optional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Position Title
                </label>
                <input
                  type="text"
                  name="currentPosition"
                  value={formData.currentPosition}
                  onChange={handleInputChange}
                  placeholder="e.g., Nurse Practitioner"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  name="currentPositionStartDate"
                  value={formData.currentPositionStartDate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Precepting Experience */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Have you precepted before? *
            </label>
            <select
              name="preceptedBefore"
              value={formData.preceptedBefore}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select an option</option>
              <option value="yes_experienced">Yes, very experienced</option>
              <option value="yes_some">Yes, some experience</option>
              <option value="first_time">This is my first time</option>
            </select>
          </div>

          {/* Schedule */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Schedule *
            </label>
            <textarea
              name="schedule"
              value={formData.schedule}
              onChange={handleInputChange}
              required
              placeholder="e.g., Monday-Friday, 9 AM - 5 PM. Flexible for rotations."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Patient Population */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Patient Population (Select all that apply) *
            </label>
            <div className="space-y-2">
              {PATIENT_POPULATIONS.map((population) => (
                <label key={population} className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.patientPopulation.includes(population)}
                    onChange={() => handlePatientPopulationChange(population)}
                    className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">{population}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-medium transition-colors"
          >
            {loading ? "Submitting..." : "Submit Application"}
          </button>

          <p className="text-xs text-gray-500 text-center">
            After submission, our team will review your application and send you account credentials within 24 hours.
          </p>
        </form>
      </div>
    </div>
  );
}

export default BecomePreceptorForm;
